import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import type { DisciplineSimple } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface DisciplineDuplicateApiOptions {
  disciplineId: string | number
  code: Discipline
  onStart?: () => void
  onSuccess?: (discipline: DisciplineSimple) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `POST /admin-api/disciplines/{discipline}/duplicate` — answers with the new discipline. */
export const useDisciplineDuplicateApi = async (
  options: DisciplineDuplicateApiOptions,
): Promise<DisciplineSimple | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.disciplines.api.duplicate', { discipline_id: options.disciplineId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, { code: options.code })
    const discipline: DisciplineSimple = data.data

    toast.success({ title: 'Successfully duplicated!', description: 'Discipline was successfully duplicated!' })
    options.onSuccess?.(discipline)

    return discipline
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error({ title: 'Discipline cannot be duplicated!', description: msg })
    options.onError?.(msg)

    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
